// Focus mode ("Hide controls"): every .chrome element fades out and the canvas
// stands alone. The bottom dock comes back while the pointer is near the top
// or bottom edge, after a tap, or while it has focus, and hides again 2.4 s
// later. Also: the fullscreen button, the restore button and the help dialog.
import type { App } from '../app.ts';
import { $ } from './dom.ts';

const REVEAL_MS = 2400;
const EDGE_TOP = 75;
const EDGE_BOTTOM = 180;

export async function toggleFullscreen(app: App): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch {
    app.store.patch({ uiHidden: true });
    app.toast('Clean view enabled. Browser fullscreen is unavailable here.', 3000);
  }
}

export function mountFocus(app: App): void {
  const body = document.body;
  const dock = $('exploreDock');
  const restore = $('restore');
  const chrome = () => Array.from(document.querySelectorAll<HTMLElement>('.chrome'));
  let revealTimer = 0;
  let dockHovered = false;

  const setInert = (el: HTMLElement, on: boolean) => { (el as HTMLElement & { inert: boolean }).inert = on; };

  const hideDock = () => {
    body.classList.remove('reveal');
    if (app.state.uiHidden) setInert(dock, true);
  };
  const reveal = () => {
    if (!app.state.uiHidden) return;
    body.classList.add('reveal');
    setInert(dock, false);
    clearTimeout(revealTimer);
    revealTimer = window.setTimeout(() => {
      if (!app.state.uiHidden) return;
      if (dockHovered || dock.contains(document.activeElement)) { reveal(); return; }
      hideDock();
    }, REVEAL_MS);
  };

  let applied: boolean | null = null;
  const apply = (hidden: boolean) => {
    if (applied === hidden) return;
    applied = hidden;
    body.classList.toggle('focus-mode', hidden);
    for (const el of chrome()) setInert(el, hidden);
    if (hidden) {
      reveal();
      app.toast('Tap the canvas or move to the bottom edge for the controls. G brings everything back.', 3200);
      app.renderer.canvas.focus({ preventScroll: true });
    } else {
      clearTimeout(revealTimer);
      body.classList.remove('reveal');
    }
  };
  app.store.subscribe((s) => apply(s.uiHidden));
  apply(app.state.uiHidden);

  // Reveal triggers.
  let down: { x: number; y: number } | null = null;
  const canvas = app.renderer.canvas;
  canvas.addEventListener('pointerdown', (ev) => { down = { x: ev.clientX, y: ev.clientY }; });
  canvas.addEventListener('pointerup', (ev) => {
    if (down && Math.hypot(ev.clientX - down.x, ev.clientY - down.y) < 6) reveal();
    down = null;
  });
  document.addEventListener('pointermove', (ev) => {
    if (!app.state.uiHidden || ev.buttons) return;
    if (ev.clientY < EDGE_TOP || ev.clientY > window.innerHeight - EDGE_BOTTOM) reveal();
  });
  dock.addEventListener('pointerenter', () => { dockHovered = true; reveal(); });
  dock.addEventListener('pointerleave', () => { dockHovered = false; reveal(); });
  dock.addEventListener('focusin', reveal);
  dock.addEventListener('focusout', reveal);

  $('btnHideUI').addEventListener('click', () => app.store.patch({ uiHidden: true }));
  restore.addEventListener('click', () => app.store.patch({ uiHidden: false }));
  $('btnFullscreen').addEventListener('click', () => void toggleFullscreen(app));
  document.addEventListener('fullscreenchange', () => app.resize());

  // Help dialog.
  const dialog = $<HTMLDialogElement>('helpDialog');
  $('btnHelp').addEventListener('click', () => dialog.showModal());
  $('closeHelp').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (ev) => {
    if (ev.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) dialog.close();
  });
}
